import { COLLISION_GROUPS } from '../../constants/Constants';
import { CharacterAttackFactory } from './attacks/CharacterAttackFactory';
import { CharacterBase } from './CharacterBase';
import { CharacterStateMachineEnemy } from './statemachines/CharacterStateMachine';

export class CharacterEnemy extends CharacterBase {
  constructor(...args) {
    super(...args);
    this._type = 'enemy';
    this._combatStyle = this._config.combatStyle;
    this._cooldownAttack = this._config.attackRate;
    this._characterAttacks = this._config.attacks.map((a) =>
      CharacterAttackFactory.create(this.scene, this, a)
    );
    this.sprite.on('event', (t, e) => {
      if (e.data.name === 'attack') {
        this.handleAttackEvent();
      }
    });
    this.bodyCollider.collisionGroup = COLLISION_GROUPS.ENEMY_BODY;
    this.attackCollider.collisionGroup = COLLISION_GROUPS.ENEMY_ATTACK;
    this.bodyCollider.setCollidesWith([COLLISION_GROUPS.HERO_ATTACK, COLLISION_GROUPS.HERO_BODY]);
    this.attackCollider.setCollidesWith([COLLISION_GROUPS.HERO_BODY]);
  }

  handleAttackEvent() {}
  createStateMachine() {
    return new CharacterStateMachineEnemy(this.scene, this);
  }

  get heroTarget() {
    return this._heroTarget;
  }

  setHeroTarget(value) {
    this._heroTarget = value;
  }

  update(delta) {
    super.update(delta);
    this._characterAttacks.forEach((a) => a.update(delta));
  }

  // =============================================================================================
  // HERO TARGET MANAGEMENT
  // =============================================================================================

  findPathToHero() {
    if (!this._heroTarget || this._pathSearching) return;

    const pathDestination = this.path?.length ? this.path[this.path.length - 1] : false;
    const targetDestination = { x: this._heroTarget.x, y: this._heroTarget.y };
    const dx = Math.abs((pathDestination ? pathDestination.x : this.x) - targetDestination.x);
    const targetDistX = Math.abs(this.x - targetDestination.x);
    const viewport = this.scene.viewport;
    const targetInLane = this.targetInLane(this._heroTarget);
    const hasRangedAttack = this.hasAvailableRangedAttack();
    if (!viewport) return;

    if (
      !viewport.contains(
        pathDestination ? pathDestination.x : this.x,
        pathDestination ? pathDestination.y : this.y
      )
    ) {
      return this.findPath(this, targetDestination);
    }

    // RANGED CLASS
    if (hasRangedAttack) {
      const rangedDist = this._heroTarget.attackRange * 2.5;
      if (targetDistX < rangedDist) {
        if (this.x > this._heroTarget.x) {
          // try an move away from target
          if (viewport.x + viewport.width < this._heroTarget.x + rangedDist) {
            // move to left of hero
            this.findPath(this, { x: this._heroTarget.x - rangedDist, y: this._heroTarget.y });
          } else {
            // move to right of hero
            this.setPath([{ x: this.x + 128, y: this.y }]);
          }
        } else {
          // try an move away from target
          if (viewport.x > this._heroTarget.x - rangedDist) {
            // move right
            this.findPath(this, { x: this._heroTarget.x + rangedDist, y: this._heroTarget.y });
          } else {
            // move left
            this.setPath([{ x: this.x - 128, y: this.y }]);
          }
        }
      } else if (targetDistX > rangedDist * 2) {
        this.findPath(this, targetDestination);
      } else if (!targetInLane) {
        this.findPath(this, { x: this.x, y: this._heroTarget.y });
      } else {
        // this.clearPath();
      }
    }

    // MELEE CLASS
    if (!hasRangedAttack || this._combatStyle === 'MELEE') {
      // back away from hero if too close
      if (targetDistX < this.attackRange * 0.25 && this.targetInLane(this._heroTarget)) {
        let dir = this._heroTarget.x > this.x ? -1 : 1;
        this.setPath([{ x: this.x + 64 * dir, y: this.y }]);
      }
      // if not same depth, move into same lane
      else if (!targetInLane) {
        this.findPath(this, { x: this.x, y: targetDestination.y });
      }
      // move towards hero if too far
      else if (dx > this.attackRange) {
        this.findPath(this, targetDestination);
      }
      // otherwise we're in a sweet spot for attacking so clear paths
      else if (Math.abs(this.x - targetDestination.x) <= this.attackRange && targetInLane) {
        this.clearPath();
      }
    }
  }

  hasAvailableRangedAttack() {
    // return this._combatStyle === 'RANGED' || this._combatStyle === 'CHARGE';
    if (this._combatStyle === 'RANGED' || this._combatStyle === 'CHARGE') {
      return this.availableRangedAttacks().length > 0;
    }
    return false;
  }

  // =============================================================================================
  // ATTACK MANAGEMENT
  // =============================================================================================

  checkForRequestAttack(delta) {
    if (this._cooldownAttack > 0) this._cooldownAttack -= delta;
    else this.requestAttack();
  }

  activateAttack(attack) {
    super.activateAttack(attack);
    if (this._attackActive) this._cooldownAttack = this.attackRate;
  }

  chooseAttack() {
    if (this.scene.game.model.noEnemyAttacks) return;

    const dx = Math.abs(this.x - this._heroTarget.x);
    const dy = Math.abs(this.y - this._heroTarget.y);
    if (dy > 32) return false;

    if (this._heroTarget.isJumping) {
      const aerialAttacks = this.availableArialAttacks();
      if (aerialAttacks) return aerialAttacks[0];
    }

    if (dx < this.attackRange) {
      const attackOptions = this.availableMeleeAttacks();
      if (attackOptions) return attackOptions[0];
    }

    const viewport = this.scene.viewport;
    if (!viewport) return false;
    if (dx > this.attackRange && this.x > viewport.x && this.x < viewport.x + viewport.width) {
      const ranged = this.availableRangedAttacks();
      if (ranged) return ranged[Math.floor(Math.random() * ranged.length)];
    }
  }

  availableArialAttacks() {
    return this.availableAttacks(CharacterBase.ATTACKS.ARIAL);
  }

  availableMeleeAttacks() {
    return this.availableAttacks(CharacterBase.ATTACKS.MELEE);
  }

  availableRangedAttacks() {
    return this.availableAttacks([CharacterBase.ATTACKS.PROJECTILE, CharacterBase.ATTACKS.CHARGE]);
  }

  availableAttacks(attacktype) {
    if (attacktype && Array.isArray(attacktype)) {
      return this._characterAttacks
        .filter((a) => attacktype.includes(a.type) && a.canTrigger)
        .sort((a, b) => b.damage - a.damage);
    }
    return this._characterAttacks
      .filter((a) => (attacktype ? a.type === attacktype && a.canTrigger : a.canTrigger))
      .sort((a, b) => b.damage - a.damage);
  }
}

CharacterEnemy.STATES = {
  IDLE: 'IDLE',
  STAGGER: 'STAGGER',
  THROW: 'THROW',
  ATTACK: 'ATTACK',
};
